'use client';

import React, { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import includes from 'lodash/includes';
import without from 'lodash/without';
import isEmpty from 'lodash/isEmpty';

import {
  ColorsSelectDataItem,
  ColorsSelectItemValue,
  ColorsSelectOnChange,
  ColorsSelectOnToggleItem
} from './ColorsSelect.types';
import { GADataType } from '../../types';

import { ColorsSelectItem } from './helpers/ColorsSelectItem';
import { ErrorFallback } from '../ErrorFallback';
import { TooltipSingletonSourceWrapper } from '../tooltips/TooltipSingletonSourceWrapper';

import { TooltipPlacement } from '../tooltips/tooltipsConstants';

export interface ColorsSelectProps {
  items: ColorsSelectDataItem[];
  isMulti?: boolean;
  value?: ColorsSelectItemValue[];
  disabled?: boolean;
  onChange: ColorsSelectOnChange;
}

function ColorsSelect({
  dataGa,
  items = [],
  isMulti = false,
  value = [],
  disabled,
  onChange
}: ColorsSelectProps & GADataType) {
  const handleToggleItem = useCallback<ColorsSelectOnToggleItem>(
    (itemValue) => {
      if (!itemValue) {
        return onChange([]);
      }

      if (isMulti) {
        return onChange(
          includes(value, itemValue)
            ? without(value, itemValue)
            : [...value, itemValue]
        );
      }

      onChange([itemValue]);
    },
    [isMulti, onChange, value]
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="text-sm">
        <div className="grid grid-cols-auto-fill grid-cell-min-8 gap-1">
          <TooltipSingletonSourceWrapper
            placement={TooltipPlacement.TOP}
            withArrow
          >
            {items.map(({ value: itemValue, label, color }) => (
              <ColorsSelectItem
                dataGa={dataGa}
                key={itemValue}
                value={itemValue}
                label={label}
                color={color}
                checked={
                  includes(value, itemValue) ||
                  (itemValue === '' && isEmpty(value))
                }
                disabled={disabled}
                onToggleItem={handleToggleItem}
              />
            ))}
          </TooltipSingletonSourceWrapper>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default ColorsSelect;
